import { Component, Suspense } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import pick from 'lodash/pick';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import { useTransl } from 'shared/lib/i18n';

import { CircularLoaderComponent } from '../LoadingSpinner/LoadingSpinnerComponent';
import SearchComponent from './SearchComponent';
import TableComponent from './TableComponent';

import { onModalClose, showConfirmModal } from '../Modal/actions';

import { formatNumber } from '../../utils';
import { lazyImport } from '../Main/utils';
import {
  filterEntries,
  getInitialParams,
  getSkeletonColumns,
  getSkeletonEntries,
  sortEntries,
  sortEntriesByKey,
} from './utils';

import * as commonSelectors from '../Shared/selectors';

import VirtualizedTable from './VirtualizedTable';

const DraggableTableComponent = lazyImport(
  () => import('./DraggableTableComponent'),
  'DraggableTableComponent',
);

const RecordsComponent = ({ sortedEntries, tableDataType, todayCount }) => {
  const { t } = useTransl();
  return (
    <>
      {formatNumber(sortedEntries?.length)}{' '}
      {tableDataType(sortedEntries?.length)}{' '}
      {todayCount !== null && todayCount >= 0
        ? ` / ${formatNumber(todayCount)} ${t('table.today')}`
        : null}
    </>
  );
};

RecordsComponent.defaultProps = {
  todayCount: null,
};

RecordsComponent.propTypes = {
  sortedEntries: PropTypes.instanceOf(Array).isRequired,
  tableDataType: PropTypes.func.isRequired,
  todayCount: PropTypes.number,
};

class SortableTable extends Component {
  constructor(props) {
    super(props);
    const { sortKey, defaultSortKey } = this.props;
    const defaultState = {
      filter: '',
      sortKey: sortKey || defaultSortKey,
    };
    const initialState = getInitialParams(defaultState);
    this.state = initialState;
    this.onFilterChange = this.onFilterChange.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
  }

  componentDidMount() {
    const { onLoad } = this.props;
    onLoad && onLoad();
  }

  onFilterChange(e) {
    this.setState({ filter: e.target.value });
  }

  onSortChange(sortKey) {
    this.setState({ sortKey });
  }

  render() {
    const { filter, sortKey } = this.state;
    const {
      tableId,
      columns,
      defaultSort,
      action,
      entries,
      hideFilterInput,
      isDraggable,
      tableDataType,
      todayCount,
      isLoading,
      onRowClick,
      rowStyles,
      getExportActions,
      isMobileScreen,
      skeletonInitialEntries,
      isVirtualizedTable,
    } = this.props;

    const showSkeleton = isLoading && !entries?.length;
    const formattedColumns = showSkeleton
      ? getSkeletonColumns(columns)
      : columns;
    const formattedEntries = showSkeleton
      ? getSkeletonEntries(skeletonInitialEntries || 20)
      : entries || [];

    const filterKeys = columns
      .filter(({ filter }) => filter)
      .map(({ dataKey }) => dataKey);
    const filteredEntries = filterEntries(formattedEntries, filterKeys, filter);

    const sortedEntries = isVirtualizedTable
      ? sortEntriesByKey(filteredEntries, columns, sortKey)
      : sortEntries(filteredEntries, sortKey);

    const exportActions = getExportActions && getExportActions(sortedEntries);

    return (
      <section>
        {filterKeys?.length > 0 && !hideFilterInput ? (
          <Box display="flex">
            <SearchComponent value={filter} onChange={this.onFilterChange} />
            {!!action && <Box ml="1rem">{action}</Box>}
          </Box>
        ) : (
          <Box display="flex" justifyContent="flex-end">
            {action}
          </Box>
        )}

        {tableDataType ? (
          <Box mx="1rem" mt="1rem">
            {showSkeleton ? (
              <Skeleton width={200} height={24} />
            ) : (
              <RecordsComponent
                sortedEntries={sortedEntries}
                tableDataType={tableDataType}
                todayCount={todayCount}
              />
            )}
          </Box>
        ) : null}

        {isDraggable ? (
          <Suspense fallback={<CircularLoaderComponent />}>
            <DraggableTableComponent
              {...pick(this.props, [
                'onReorder',
                'showModal',
                'closeModal',
                'defaultSort',
                'columns',
                'tableId',
              ])}
              entries={sortedEntries}
            />
          </Suspense>
        ) : (
          <>
            {isVirtualizedTable ? (
              <VirtualizedTable
                entries={sortedEntries}
                columns={formattedColumns}
                onRowClick={onRowClick}
                sortKey={sortKey}
                onSortChange={this.onSortChange}
              />
            ) : (
              <TableComponent
                tableId={tableId}
                entries={sortedEntries}
                columns={formattedColumns}
                defaultSort={defaultSort}
                onRowClick={onRowClick}
                rowStyles={rowStyles}
                isMobileScreen={isMobileScreen}
              />
            )}
            {exportActions && !!sortedEntries?.length && (
              <Box my="1rem" width="8rem">
                {exportActions}
              </Box>
            )}
          </>
        )}
      </section>
    );
  }
}

SortableTable.defaultProps = {
  action: null,
  rowStyles: null,
  defaultSortKey: null,
  tableDataType: null,
  onLoad: null,
  onRowClick: null,
  todayCount: null,
  exportActions: null,
  hideFilterInput: false,
  isDraggable: false,
};

SortableTable.propTypes = {
  action: PropTypes.instanceOf(Object),
  rowStyles: PropTypes.instanceOf(Object),
  columns: PropTypes.instanceOf(Array).isRequired,
  entries: PropTypes.instanceOf(Array).isRequired,
  tableId: PropTypes.string.isRequired,
  defaultSort: PropTypes.instanceOf(Array),
  defaultSortKey: PropTypes.string,
  tableDataType: PropTypes.func,
  hideFilterInput: PropTypes.bool,
  isDraggable: PropTypes.bool,
  onLoad: PropTypes.func,
  onRowClick: PropTypes.func,
  todayCount: PropTypes.number,
  exportActions: PropTypes.node,
  isLoading: PropTypes.bool.isRequired,
  isMobileScreen: PropTypes.bool.isRequired,
  getExportActions: PropTypes.func,
  skeletonInitialEntries: PropTypes.number,
  isVirtualizedTable: PropTypes.bool,
  sortKey: PropTypes.string,
};

const mapStateToProps = (state) => ({
  todayCount: commonSelectors.getTodayCount(state),
  isLoading: commonSelectors.getIsLoading(state),
  isMobileScreen: commonSelectors.getIsMobileScreen(state),
});
const mapDispatchToProps = (dispatch) => ({
  showModal: (modalProps) => showConfirmModal(dispatch, modalProps),
  closeModal: () => onModalClose(dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(SortableTable));
